'use client';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import {
  languages,
  LanguageType,
  countries,
  CountryType,
} from '@/constant/localeData';
import useLocaleChange from '@/hooks/useLocaleChange';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

interface CountrySelectorProps {
  openMenuClass?: string;
}

const flags: { [key: string]: { src: string; alt: string } } = {
  en: {
    src: '/countries/en.png',
    alt: 'English flag',
  },
  de: {
    src: '/german.png',
    alt: 'German flag',
  },
};

const CountrySelector = ({ openMenuClass }: CountrySelectorProps) => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const { changeLanguage, changeCountry, defaultLocale } = useLocaleChange();
  const handleOpen = (): void => {
    setOpenMenu(!openMenu);
  };

  // When focused on outside -> close
  const dropDownRef: any = useRef();
  useEffect((): void => {
    const handleClickOutside = (event: any): void => {
      if (!dropDownRef?.current?.contains(event.target)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
  }, [dropDownRef]);

  return (
    <div className="relative h-[20px] w-[140px] lg:pl-2" ref={dropDownRef}>
      <div
        className="flex cursor-pointer items-center"
        onClick={handleOpen}
        role="button"
      >
        {defaultLocale.country &&
          defaultLocale.lang &&
          defaultLocale.lang.key && (
            <Image
              src={flags[defaultLocale.lang.key].src}
              alt={flags[defaultLocale.lang.key].alt}
              width={20}
              height={12}
              className="mr-[10px]"
            />
          )}
        <span className="min-w-[55px] text-[15px]/[1.3] text-paragraph">
          {defaultLocale?.lang && defaultLocale.lang.title}
        </span>
        <div
          className={`ml-[6px] flex h-[20px] w-[20px] transform items-center justify-center transition-transform duration-300 ease-in-out ${
            openMenu
              ? 'rotate-0 text-primary-700'
              : 'rotate-180 text-neutral-400'
          }`}
        >
          <svg
            width="16"
            height="8"
            viewBox="0 0 16 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.8337 6.68519L9.03147 1.9219C8.46434 1.35937 7.53631 1.35937 6.96918 1.9219L2.16699 6.68518"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      {openMenu ? (
        <div
          className={`${openMenuClass} absolute left-0 right-0 top-[100%] z-30 mt-[16px] flex h-auto flex-col justify-between space-y-4 overflow-hidden overflow-y-auto rounded-[10px] bg-white p-[30px] shadow-[0px_10px_30px_rgba(0,0,0,0.1)] sm:flex-row sm:space-y-0`}
        >
          {/* Classname removed from this div ⬆︎ -> "w-[250px] sm:w-[338px]" */}

          {/* Hide country selector for now as per stakeholder request */}
          {/* <div>
            <h4 className="mb-[10px] text-h7 font-[700] text-title">Country</h4>
            <ul>
              {countries.map((country: CountryType, i) => (
                <li
                  key={country?.id}
                  className="flex cursor-pointer items-center py-[10px] text-base text-[#646464]"
                  onClick={() => changeCountry(country.code)}
                >
                  <Image
                    src={country?.countryFlagUrl}
                    alt={country?.countryName}
                    width={20}
                    height={12}
                    className="mr-[10px]"
                  />
                  <span>{country?.countryName}</span>
                  {defaultLocale?.country &&
                    defaultLocale.country.code === country.code && (
                      <Image
                        src="/check.png"
                        alt="Check"
                        width={16}
                        height={16}
                        className="ml-[10px]"
                      />
                    )}
                </li>
              ))}
            </ul>
          </div>
          <span className="inline-block w-full self-center border border-gray-100 bg-neutral-100 sm:h-[113px] sm:w-[1px] sm:border-0" /> */}
          <div>
            <h4 className="mb-[10px] text-h7 font-[700] text-title">
              Language
            </h4>
            <ul>
              {languages.map((lang: LanguageType, i: number) => (
                <li
                  key={lang.key}
                  className="flex cursor-pointer items-center py-[10px] text-base text-[#646464]"
                  onClick={() => changeLanguage(lang.key)}
                >
                  <span>{lang.title}</span>
                  {defaultLocale?.lang &&
                    lang.key === defaultLocale.lang.key && (
                      <CheckCircleIcon className="ml-2 h-5 w-5 text-green-500" />
                    )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CountrySelector;
